import React, { useState } from 'react';
import { Eye, Cookie, AlertCircle, CheckCircle, X, ExternalLink, Loader2 } from 'lucide-react';
import * as fbMonitorApi from '../../api/fbMonitor';

const SessionSetup = ({ onSessionCreated, onCancel, isReconnect = false }) => {
  const [cookies, setCookies] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Basic validation
    const trimmedCookies = cookies.trim();
    if (!trimmedCookies) {
      setError('Please paste your Facebook cookies');
      return;
    }

    // Parse JSON and validate format
    let cookiesArray;
    try {
      cookiesArray = JSON.parse(trimmedCookies);
    } catch {
      setError('Invalid cookie format. Please paste valid JSON cookies.');
      return;
    }

    // Validate it's an array
    if (!Array.isArray(cookiesArray)) {
      setError('Cookies must be a JSON array.');
      return;
    }

    setIsSubmitting(true);
    try {
      await fbMonitorApi.createSession({
        cookies: cookiesArray,
        user_agent: navigator.userAgent,
      });
      onSessionCreated();
    } catch (err) {
      setError(err.message || 'Failed to create session');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
          <Eye className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold dark:text-white text-slate-900 mb-2">
          {isReconnect ? 'Reconnect Facebook Session' : 'Setup FB Content Monitor'}
        </h1>
        <p className="dark:text-slate-400 text-slate-500">
          {isReconnect
            ? 'Your session has expired. Please reconnect to continue monitoring.'
            : 'Connect your Facebook session to start monitoring groups and pages for mortgage leads.'
          }
        </p>
      </div>

      {/* Instructions */}
      <div className="dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-700 border-slate-200 rounded-xl p-6 mb-6">
        <h2 className="text-lg font-semibold dark:text-white text-slate-900 mb-4 flex items-center gap-2">
          <Cookie className="w-5 h-5 text-amber-500" />
          How to Get Your Cookies
        </h2>
        <ol className="space-y-3 text-sm dark:text-slate-300 text-slate-600">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-bold">1</span>
            <span>Install a browser extension like "Cookie-Editor" or "EditThisCookie"</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-bold">2</span>
            <span>Go to <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:text-sky-400 inline-flex items-center gap-1">facebook.com <ExternalLink className="w-3 h-3" /></a> and make sure you're logged in</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-bold">3</span>
            <span>Open the cookie extension and click "Export" or "Export as JSON"</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-bold">4</span>
            <span>Paste the exported JSON below</span>
          </li>
        </ol>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
            Facebook Cookies (JSON)
          </label>
          <textarea
            value={cookies}
            onChange={(e) => setCookies(e.target.value)}
            placeholder='[{"name": "c_user", "value": "...", ...}]'
            rows={8}
            className="w-full rounded-lg px-4 py-3 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-white text-slate-900 font-mono text-sm"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-start gap-3 p-4 rounded-lg dark:bg-red-500/10 bg-red-50 border dark:border-red-500/30 border-red-200">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm dark:text-red-400 text-red-600">{error}</p>
          </div>
        )}

        {/* Privacy Notice */}
        <div className="flex items-start gap-3 p-4 rounded-lg dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-700 border-slate-200">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm dark:text-slate-400 text-slate-500">
            Your cookies are encrypted and stored securely. We only use them to read public posts from groups and pages you specify. We never post, message, or take any actions on your behalf.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-4">
          {isReconnect && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 dark:text-slate-400 text-slate-600 hover:dark:text-white hover:text-slate-900 font-medium rounded-lg transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting || !cookies.trim()}
            className="flex-1 px-6 py-3 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Eye className="w-5 h-5" />
                {isReconnect ? 'Reconnect Session' : 'Connect & Start Monitoring'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SessionSetup;
